import { defineStyle, defineStyleConfig, ComponentStyleConfig } from '@chakra-ui/react';
import { transparentize } from '@chakra-ui/theme-tools'; // transparentize appears to be replaced with colorMix in chakra v3, currently still in development

const sizes = {
  xs: {
    fontSize: 'xs',
    lineHeight: 4
  },
  sm: {
    fontSize: 'sm',
    lineHeight: 4
  },
  md: {
    fontSize: 'md',
    lineHeight: 5
  },
  lg: {
    fontSize: 'lg',
    lineHeight: 6
  },
  xl: {
    fontSize: 'xl',
    lineHeight: 7
  }
};

const variantSecondary = defineStyle(props => {
  const { colorScheme, theme } = props;

  return {
    color: `${colorScheme}.600`,
    bgColor: 'utility.100',

    _hover: {
      color: `${colorScheme}.700`,
      bgColor: `${colorScheme}.100`,

      _disabled: {
        bgColor: 'utility.100'
      }
    },

    _active: {
      color: `${colorScheme}.800`,
      bgColor: `${colorScheme}.200`
    },

    _dark: {
      color: `${colorScheme}.300`,
      bgColor: 'whiteAlpha.100',

      _hover: {
        color: `${colorScheme}.200`,
        bgColor: transparentize(`${colorScheme}.200`, 0.16)(theme),

        _disabled: {
          bgColor: transparentize(`${colorScheme}.200`, 0.16)(theme)
        }
      },

      _active: {
        color: `${colorScheme}.100`,
        bgColor: transparentize(`${colorScheme}.200`, 0.32)(theme)
      }
    }
  };
});

const variantPrimary = {
  '.utility-icon': {
    color: 'whiteAlpha.800'
  },

  _dark: {
    '.utility-icon': {
      color: 'utility.600'
    }
  },

  _light: {
    _focusVisible: {
      boxShadow: 'outlineInverse'
    }
  }
};

const variantTertiary = defineStyle(props => {
  const { colorScheme, theme } = props;

  return {
    color: `${colorScheme}.600`,

    _hover: {
      color: `${colorScheme}.700`,
      bgColor: `${colorScheme}.100`
    },

    _active: {
      color: `${colorScheme}.800`,
      bgColor: `${colorScheme}.200`
    },

    _dark: {
      color: `${colorScheme}.300`,

      _hover: {
        color: `${colorScheme}.200`,
        bgColor: transparentize(`${colorScheme}.200`, 0.16)(theme),

        _disabled: {
          bgColor: 'initial'
        }
      },

      _active: {
        color: `${colorScheme}.100`,
        bgColor: transparentize(`${colorScheme}.200`, 0.32)(theme)
      }
    }
  };
});

const variantNavbar = defineStyle({
  bgColor: 'transparent',
  color: 'whiteAlpha.800',
  height: 9,
  px: 4,

  '.utility-icon': {
    color: 'whiteAlpha.600'
  },

  '.nav-icon': {
    color: 'whiteAlpha.600'
  },

  _hover: {
    color: 'whiteAlpha.900',
    bgColor: 'whiteAlpha.200'
  },

  _active: {
    color: 'whiteAlpha.900',
    bgColor: 'blackAlpha.400',

    '.utility-icon': {
      color: 'whiteAlpha.800'
    },

    '.nav-icon': {
      color: 'yellow.500'
    }
  },

  _light: {
    _focusVisible: {
      boxShadow: 'outlineInverse'
    }
  }
});

const variantSubnav = defineStyle({
  color: 'utility.800',
  justifyContent: 'start',
  textAlign: 'left',

  '.nav-icon': {
    color: 'utility.500'
  },

  _hover: {
    bgColor: 'utility.100',
    color: 'utility.900'

    // '.nav-icon': {
    //   color: 'utility.500'
    // },
  },

  _active: {
    bgColor: 'blue.200',
    color: 'utility.900',

    '.nav-icon': {
      color: 'blue.500'
    }
  },

  _dark: {
    color: 'whiteAlpha.800',

    '.nav-icon': {
      color: 'whiteAlpha.500'
    },

    _hover: {
      bgColor: 'whiteAlpha.100',
      color: 'whiteAlpha.900',

      _disabled: {
        bgColor: 'initial',
        color: 'whiteAlpha.700'
      }
    },

    _active: {
      bgColor: 'whiteAlpha.200',
      color: 'whiteAlpha.900',

      '.nav-icon': {
        color: 'whiteAlpha.700'
      }
    }
  }
});

const variantMobileNav = defineStyle({
  bgColor: 'transparent',
  borderRadius: 0,
  color: 'utility.800',
  flexDirection: 'column',
  fontSize: 'xs',
  height: 'auto',
  pt: '1rem',
  pb: '1rem',
  px: 0,
  width: '100%',
  flexShrink: '1',
  minWidth: '4rem',
  maxWidth: '8rem',

  '.chakra-button__icon': {
    fontSize: '1.25rem',
    marginEnd: 0,
    mb: 2
  },

  '.nav-icon': {
    color: 'utility.500'
  },

  _hover: {
    bgColor: 'utility.100',
    color: 'blue.700',

    '.nav-icon': {
      color: 'blue.500'
    }
  },

  _active: {
    bgColor: 'blue.100',
    color: 'blue.800',

    '.nav-icon': {
      color: 'blue.600'
    }
  },

  _dark: {
    color: 'whiteAlpha.700',

    '.nav-icon': {
      color: 'whiteAlpha.700'
    },

    _hover: {
      bgColor: 'whiteAlpha.100',
      color: 'whiteAlpha.900',

      '.nav-icon': {
        color: 'whiteAlpha.700'
      }
    },

    _active: {
      bgColor: 'whiteAlpha.200',
      color: 'whiteAlpha.900',

      '.nav-icon': {
        color: 'white'
      }
    }
  }
});

const variantOutline = defineStyle(props => {
  const { colorScheme } = props;

  return {
    _dark: {
      borderColor: `${colorScheme}.600`,
      color: `${colorScheme}.300`,

      _hover: {
        bgColor: `${colorScheme}.700`,
        color: `${colorScheme}.200`,

        _disabled: {
          bgColor: 'initial'
        }
      },

      _active: {
        bgColor: `${colorScheme}.600`,
        color: `${colorScheme}.100`
      }
    }
  };
});

const variantSidebar = defineStyle(props => {
  const { colorScheme } = props;
  return {
    ...variantSubnav,
    display: 'flex',
    justifyContent: 'space-between',
    textAlign: 'left',
    px: 'sidebarPx',
    borderRadius: 0,

    _hover: {
      bgColor: `${colorScheme}.100`,
      color: `${colorScheme}.800`
    },

    _active: {
      bgColor: `${colorScheme}.200`,
      color: `${colorScheme}.900`
    }
  };
});

const variantQuickLink = defineStyle(props => {
  const { colorScheme } = props;
  return {
    ...variantSubnav,

    _hover: {
      bgColor: `${colorScheme}.50`
    },

    _active: {
      bgColor: `${colorScheme}.100`,
      color: `${colorScheme}.900`
    }
  };
});

const variantStatus = defineStyle(props => {
  const { colorScheme, theme } = props;

  return {
    color: `${colorScheme}.800`,
    bgColor: `${colorScheme}.100`,

    _hover: {
      color: `${colorScheme}.900`,
      bgColor: `${colorScheme}.200`,

      _disabled: {
        opacity: 0.6
      }
    },

    _active: {
      color: `${colorScheme}.900`,
      bgColor: `${colorScheme}.300`
    },

    _dark: {
      color: `${colorScheme}.300`,
      bgColor: transparentize(`${colorScheme}.100`, 0.16)(theme),

      _hover: {
        color: `${colorScheme}.200`,
        bgColor: transparentize(`${colorScheme}.200`, 0.16)(theme),

        _disabled: {
          bgColor: transparentize(`${colorScheme}.200`, 0.16)(theme)
        }
      },

      _active: {
        color: `${colorScheme}.100`,
        bgColor: transparentize(`${colorScheme}.200`, 0.32)(theme)
      }
    }
  };
});

export const buttonTheme: ComponentStyleConfig = defineStyleConfig({
  baseStyle: {
    borderRadius: 'lg',
    flexShrink: '0',
    fontWeight: 'medium',
    letterSpacing: 'tight',

    _hover: {
      textDecoration: 'none'
    },

    _disabled: {
      opacity: 0.65
    }
  },
  defaultProps: {
    colorScheme: 'blue',
    variant: 'secondary'
  },
  sizes: sizes,
  variants: {
    solid: variantPrimary,
    secondary: variantSecondary,
    ghost: variantTertiary,
    outline: variantOutline,
    navbar: variantNavbar,
    subnav: variantSubnav,
    sidebar: variantSidebar,
    mobile: variantMobileNav,
    quicklink: variantQuickLink,
    status: variantStatus
  }
});
